"use client";

/**
 * Sticky banner shown ABOVE the topbar whenever the current session
 * is the result of an admin "Prise de contrôle". Stays visible on
 * every page so the actor can't forget they're acting under another
 * identity. The "Sortir du compte simulé" button restores the actor's
 * original tokens (snapshotted at takeover) and hard-reloads.
 *
 * Mounted at the very top of the chrome layout so the rest of the
 * site shifts down — important: no fixed positioning, otherwise it
 * would overlap the topbar on scroll. The cream/burgundy palette is
 * deliberately loud so it can't blend into the page chrome.
 */

import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import {
  hydrateImpersonation,
  stopImpersonation,
} from "@/app/lib/reducers/impersonationReducer";

const ImpersonationBanner: FC = () => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();
  const active = useAppSelector((s: any) => s?.impersonation?.active);
  const [exiting, setExiting] = useState(false);

  // Hydrate from localStorage on mount — the Redux state is fresh
  // after a hard reload, but the impersonation snapshot persists.
  useEffect(() => {
    dispatch(hydrateImpersonation());
  }, [dispatch]);

  if (!active) return null;

  const fullName =
    `${active.targetFirstName || ""} ${active.targetLastName || ""}`.trim() ||
    (t("impersonation_no_name", { defaultValue: "(sans nom)" }) as string);

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await dispatch(stopImpersonation() as any);
    } catch {
      /* The localStorage swap already happened in stopImpersonation —
         a backend audit failure shouldn't block the actor's exit. */
    }
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        background: "var(--g-burgundy)",
        color: "white",
        padding: "10px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        fontSize: 13,
        boxShadow: "0 2px 6px rgba(0,0,0,.18)",
        position: "relative",
        zIndex: 60,
      }}
    >
      <div className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "rgba(255,255,255,.18)",
            border: "1px solid rgba(255,255,255,.35)",
            fontSize: 14,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          👁
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, lineHeight: 1.2 }}>
            {t("impersonation_banner_connected_as", {
              defaultValue: "Connecté en tant que",
            })}{" "}
            <span style={{ textDecoration: "underline" }}>{fullName}</span>
          </div>
          {active.targetEmail && (
            <div
              style={{
                fontSize: 11,
                opacity: 0.88,
                marginTop: 2,
                wordBreak: "break-all",
              }}
            >
              {active.targetEmail}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={handleExit}
        disabled={exiting}
        style={{
          flexShrink: 0,
          height: 36,
          padding: "0 16px",
          borderRadius: 999,
          background: "white",
          color: "var(--g-burgundy)",
          border: 0,
          fontWeight: 700,
          fontSize: 12,
          cursor: exiting ? "wait" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {exiting
          ? (t("impersonation_exiting", {
              defaultValue: "Sortie…",
            }) as string)
          : (t("impersonation_exit_button", {
              defaultValue: "Sortir du compte simulé",
            }) as string)}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
